import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useSport } from "@/context/SportContext";
import { Button } from "@/components/ui/button";
import { LogOut, User, Plus, ChevronDown, MessageSquare, Settings, Bookmark, Calendar, LocateFixed, Loader2, Bell, Shield, Lightbulb } from "lucide-react";
import { titleColor, isProUser } from "@/lib/profiles";
import NotificationsBell from "@/components/NotificationsBell";
import AdminNavLink from "@/components/AdminNavLink";
import TierPortal from "@/components/TierPortal";
import ThemeToggle from "@/components/ThemeToggle";
import FollowingDrawer from "@/components/FollowingDrawer";
import HeaderSearch from "@/components/HeaderSearch";
import { useMyLoc, requestMyLocation } from "@/lib/geoLocation";
import { useState } from "react";
import FeatureRequestModal from "@/components/FeatureRequestModal";

const TEAM_ROLES = new Set(["team_director", "scout"]);

// Icon shown to logged-out visitors — same look as the real ones, but tapping it
// prompts them to log in / sign up instead of performing the action.
function PromptIcon({ icon: Icon, label, testid, onClick }) {
    return (
        <button
            type="button"
            data-testid={testid}
            onClick={onClick}
            title={label}
            aria-label={label}
            className="inline-flex items-center justify-center h-9 w-9 text-white/80 hover:text-[#ff4500] transition-colors"
        >
            <Icon className="h-5 w-5" />
        </button>
    );
}

// Locate button — requests precise device location, shared site-wide.
function HeaderLocateButton() {
    const [loc] = useMyLoc();
    const [locating, setLocating] = useState(false);
    const onLocate = async () => {
        setLocating(true);
        try { await requestMyLocation(); } finally { setLocating(false); }
    };
    return (
        <button
            type="button"
            data-testid="header-locate-btn"
            onClick={onLocate}
            disabled={locating}
            title={loc ? "Using your location" : "Use my location"}
            aria-label="Use my location"
            className="inline-flex items-center justify-center h-9 w-9 transition-colors disabled:opacity-60 text-white/80 hover:text-[#ff4500]"
        >
            {locating ? <Loader2 className="h-5 w-5 animate-spin" /> : <LocateFixed className="h-5 w-5" />}
        </button>
    );
}

export default function Header() {
    const { user, logout, feedBadgeTotal } = useAuth();
    const navigate = useNavigate();
    const { sport } = useSport();
    const isPlayerActive = user && user.active_profile === "player";
    const [profileMenuOpen, setProfileMenuOpen] = useState(false);
    const [followingOpen, setFollowingOpen] = useState(false);
    const [authPromptOpen, setAuthPromptOpen] = useState(false);
    const [featureOpen, setFeatureOpen] = useState(false);
    const openFeature = () => {
        if (!user) { navigate(`/login?next=${encodeURIComponent("/")}`); return; }
        setFeatureOpen(true);
    };
    const openPrompt = () => setAuthPromptOpen(true);
    const uname = user ? (user.username || (user.email || "").split("@")[0]) : "";

    const goAdd = () => {
        const target = `/add/${encodeURIComponent(sport || "Basketball")}?type=event`;
        if (!user) navigate(`/login?next=${encodeURIComponent(target)}`);
        else navigate(target);
    };

    return (
        <>
        <header
            data-testid="site-header"
            className="sticky top-0 z-[1100] backdrop-blur-xl bg-black/60 border-b border-white/10"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 py-3 flex flex-wrap items-center gap-x-3 gap-y-2">
                <Link to="/" data-testid="logo-link" className="order-1 flex items-center gap-2 group min-w-0 shrink">
                    <img
                        data-testid="brand-mark"
                        src="/tsi-mark.png"
                        alt="TSI"
                        className="h-4 w-4 sm:h-6 sm:w-6 md:h-7 md:w-7 shrink-0 object-contain group-hover:opacity-90 transition-opacity"
                    />
                    <div className="min-w-0">
                        <div data-testid="site-brand" className="font-display text-base leading-none tracking-tight whitespace-nowrap sm:text-lg sm:tracking-wide md:text-2xl">
                            TRAVEL SPORTS<span className="text-[#ff4500] text-[1.5em] leading-none">.</span><span className="text-[#ff4500]">INFO</span>
                        </div>
                    </div>
                </Link>

                <button
                    type="button"
                    data-testid="header-feature-request-btn"
                    onClick={openFeature}
                    title="Request a feature"
                    aria-label="Request a feature"
                    className="order-2 ml-auto shrink-0 rounded-none bg-black border border-[#3a3a3a] text-[#9ca3af] hover:text-white hover:border-[#4a4a4a] h-9 w-9 inline-flex items-center justify-center transition-colors"
                >
                    <Lightbulb className="h-4 w-4" />
                </button>

                <Button
                    data-testid="header-add-btn"
                    onClick={goAdd}
                    size="sm"
                    variant="ghost"
                    title="Add a listing"
                    aria-label="Add a listing"
                    className="order-3 shrink-0 rounded-none bg-black border border-[#ff4500] text-[#ff4500] hover:bg-[#ff4500]/10 hover:text-[#ff4500] uppercase tracking-wider h-9 px-2 inline-flex items-center gap-1"
                >
                    <Plus className="h-4 w-4" /> Add
                </Button>

                <nav data-testid="header-nav" className="order-4 basis-full flex flex-wrap items-center justify-end gap-1.5 sm:gap-2 md:gap-3 min-w-0">
                    {user?.role === "player_parent" && isPlayerActive && (
                        <Link
                            to="/profile"
                            data-testid="nav-profile"
                            className="hidden md:block text-sm uppercase tracking-wider text-white/80 hover:text-[#ff4500] transition-colors px-3 py-2"
                        >
                            My Profile
                        </Link>
                    )}
                    <HeaderSearch />
                    {user ? <HeaderLocateButton /> : <PromptIcon icon={LocateFixed} label="Use my location" testid="nav-locate-guest" onClick={openPrompt} />}
                    {user ? (
                        <Link
                            to="/feed"
                            data-testid="nav-feed"
                            title="Feed"
                            aria-label="Feed"
                            className="relative inline-flex items-center justify-center h-9 w-9 text-white/80 hover:text-[#ff4500] transition-colors"
                        >
                            <MessageSquare className="h-5 w-5" />
                            {feedBadgeTotal > 0 && (
                                <span data-testid="nav-feed-badge" className="absolute -top-0.5 right-0.5 min-w-[16px] h-4 px-1 bg-[#ff4500] text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                                    {feedBadgeTotal > 9 ? "9+" : feedBadgeTotal}
                                </span>
                            )}
                        </Link>
                    ) : (
                        <PromptIcon icon={MessageSquare} label="Feed" testid="nav-feed-guest" onClick={openPrompt} />
                    )}
                    {user?.is_admin && <AdminNavLink />}
                    {user ? (
                        <>
                            <NotificationsBell />
                            <button
                                type="button"
                                data-testid="following-menu-btn"
                                onClick={() => setFollowingOpen(true)}
                                title="Following"
                                aria-label="Open following list"
                                className="inline-flex items-center justify-center h-9 w-9 text-white/80 hover:text-[#ff4500] transition-colors"
                            >
                                <Bookmark className="h-5 w-5" />
                            </button>
                            <Link
                                to="/favorites"
                                data-testid="nav-following"
                                title="Following"
                                aria-label="Following"
                                className="inline-flex items-center justify-center h-9 w-9 text-[#a0a0a0] hover:text-[#ff4500] transition-colors"
                            >
                                <Calendar className="h-5 w-5" />
                            </Link>
                            <div className="flex items-center relative border-l border-white/10 pl-2 md:pl-4">
                                <button
                                    type="button"
                                    data-testid="header-profile-link"
                                    aria-label="Account menu"
                                    onClick={() => setProfileMenuOpen((v) => !v)}
                                    className="group inline-flex items-center gap-2 text-xs text-[#e5e5e5]"
                                >
                                    <span className="shrink-0 group-hover:opacity-80 transition-opacity">
                                        <TierPortal count={user.thanks_count || 0} size={24} ringW={2} gap={2} testid="header-avatar-portal">
                                            {user.photo ? (
                                                <img src={user.photo} alt="" data-testid="header-avatar" className="h-full w-full object-cover" />
                                            ) : (
                                                <User className="h-3.5 w-3.5 text-[#a0a0a0]" />
                                            )}
                                        </TierPortal>
                                    </span>
                                    <span className="flex flex-col items-start leading-tight">
                                        <span className="inline-flex items-center gap-1">
                                            <span
                                                data-testid="header-username"
                                                className="font-semibold group-hover:text-[#ff4500] transition-colors truncate min-w-0 max-w-[84px] sm:max-w-[140px] md:max-w-[160px] lg:max-w-[220px]"
                                            >
                                                {uname}
                                            </span>
                                            <span data-testid="header-menu-toggle" className="inline-flex items-center text-[#a0a0a0] group-hover:text-[#ff4500] transition-colors">
                                                <ChevronDown className="h-3 w-3" />
                                            </span>
                                        </span>
                                        {user.pro_title ? (
                                            <span
                                                data-testid="header-title"
                                                className="uppercase tracking-[0.15em] text-[10px] font-bold"
                                                style={{ color: titleColor(user.pro_title, isProUser(user)) }}
                                            >
                                                {user.pro_title}
                                            </span>
                                        ) : null}
                                    </span>
                                </button>
                                {profileMenuOpen && (
                                    <div
                                        data-testid="account-menu"
                                        className="absolute right-0 top-full mt-2 min-w-[200px] bg-[#141414] border border-[#2a2a2a] shadow-xl z-[1200]"
                                        onMouseLeave={() => setProfileMenuOpen(false)}
                                    >
                                        <Link
                                            to={`/u/${uname}`}
                                            onClick={() => setProfileMenuOpen(false)}
                                            data-testid="menu-my-profile"
                                            className="flex items-center gap-2 px-3 py-2.5 text-sm text-[#e5e5e5] hover:bg-[#0f0f0f] hover:text-[#ff4500] transition-colors"
                                        >
                                            <User className="h-4 w-4" />
                                            <span>My Profile Page</span>
                                        </Link>
                                        <Link
                                            to="/account"
                                            onClick={() => setProfileMenuOpen(false)}
                                            data-testid="menu-account"
                                            className="flex items-center gap-2 px-3 py-2.5 border-t border-[#2a2a2a] text-sm text-[#e5e5e5] hover:bg-[#0f0f0f] hover:text-[#ff4500] transition-colors"
                                        >
                                            <Settings className="h-4 w-4" />
                                            <span>Account management</span>
                                        </Link>
                                        {user?.is_admin && (
                                            <Link
                                                to="/admin"
                                                onClick={() => setProfileMenuOpen(false)}
                                                data-testid="menu-admin"
                                                className="flex items-center gap-2 px-3 py-2.5 border-t border-[#2a2a2a] text-sm text-[#ff4500] hover:bg-[#0f0f0f] hover:text-white transition-colors font-semibold"
                                            >
                                                <Shield className="h-4 w-4" />
                                                <span>Admin Dashboard</span>
                                            </Link>
                                        )}
                                        <ThemeToggle variant="menu" />
                                        <button
                                            type="button"
                                            data-testid="logout-btn"
                                            onClick={async () => { setProfileMenuOpen(false); await logout(); navigate("/"); }}
                                            className="w-full flex items-center gap-2 px-3 py-2.5 border-t border-[#2a2a2a] text-sm text-[#e5e5e5] hover:bg-[#0f0f0f] hover:text-[#ff4500] transition-colors"
                                        >
                                            <LogOut className="h-4 w-4" />
                                            <span>Log out</span>
                                        </button>
                                    </div>
                                )}
                            </div>
                        </>
                    ) : (
                        <>
                            <PromptIcon icon={Bell} label="Notifications" testid="nav-notifications-guest" onClick={openPrompt} />
                            <PromptIcon icon={Bookmark} label="Following" testid="nav-following-guest" onClick={openPrompt} />
                            <PromptIcon icon={Calendar} label="Saved" testid="nav-saved-guest" onClick={openPrompt} />
                            <Link to="/login" data-testid="nav-login" className="border-l border-white/10 pl-2 md:pl-3">
                                <Button variant="ghost" size="sm" className="rounded-none uppercase tracking-wider text-white hover:bg-white/5 hover:text-[#ff4500]">
                                    Log in
                                </Button>
                            </Link>
                            <Link to="/register" data-testid="nav-register">
                                <Button size="sm" className="rounded-none bg-[#ff4500] hover:bg-[#ff5722] uppercase tracking-wider text-white">
                                    Sign up
                                </Button>
                            </Link>
                        </>
                    )}
                </nav>
            </div>
        </header>
        <FeatureRequestModal open={featureOpen} onClose={() => setFeatureOpen(false)} />
        {user && <FollowingDrawer open={followingOpen} onClose={() => setFollowingOpen(false)} />}
        {authPromptOpen && (
            <div className="fixed inset-0 z-[1300] bg-black/70 flex items-center justify-center p-4" data-testid="auth-prompt" onClick={() => setAuthPromptOpen(false)}>
                <div className="w-full max-w-sm bg-[#141414] border border-[#2a2a2a] p-6" onClick={(e) => e.stopPropagation()}>
                    <h3 className="text-lg font-bold text-white">Log in to continue</h3>
                    <p className="text-sm text-[#a0a0a0] mt-1.5">Create a free account or log in to use search, your location, the feed, notifications and following.</p>
                    <div className="mt-5 flex gap-2">
                        <Button
                            data-testid="auth-prompt-login"
                            onClick={() => { setAuthPromptOpen(false); navigate("/login"); }}
                            variant="ghost"
                            className="flex-1 rounded-none border border-white/15 uppercase tracking-wider text-white hover:bg-white/5 hover:text-[#ff4500]"
                        >
                            Log in
                        </Button>
                        <Button
                            data-testid="auth-prompt-signup"
                            onClick={() => { setAuthPromptOpen(false); navigate("/register"); }}
                            className="flex-1 rounded-none bg-[#ff4500] hover:bg-[#ff5722] uppercase tracking-wider text-white"
                        >
                            Sign up
                        </Button>
                    </div>
                    <button
                        type="button"
                        data-testid="auth-prompt-dismiss"
                        onClick={() => setAuthPromptOpen(false)}
                        className="mt-3 w-full text-center text-[11px] uppercase tracking-[0.15em] text-[#777] hover:text-white transition-colors"
                    >
                        Maybe later
                    </button>
                </div>
            </div>
        )}
        </>
    );
}
